// useLinkOrCreate.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { createNode as createNodeFromType } from 'utilities/NodeFactory';

import { subscribeToNodesByDatabase } from 'services/nodes/NodeReadService';
import { listAdjacentNodeKeys, upsertEdge } from 'services/nodes/NodeEdgeService';
import { createNode as createNodeDoc } from 'services/nodes/NodeWriteService';
import { nodeMatchesSourceType } from 'components/addmenu/addMenuTypeFilters';
import { buildHierarchyEdgeOptions } from 'utilities/edgeHierarchy';

const getNodeTitle = (node) => String(node?.title || '').trim() || 'Untitled';

function normalizeTypeKeys({ typeKey, typeKeys } = {}) {
  const fromList = (Array.isArray(typeKeys) ? typeKeys : [])
    .map((key) => String(key || '').trim())
    .filter(Boolean);
  if (fromList.length > 0) return Array.from(new Set(fromList));
  const single = String(typeKey || '').trim();
  return single ? [single] : [];
}

/**
 * useLinkOrCreate
 *
 * Loads nodes of a selected type, filters out already-related items,
 * and handles attach-or-create for the related-item add flow.
 */
export function useLinkOrCreate({
  isOpen,
  isActive,
  typeKey,
  typeKeys = null,
  sourceType = '',
  baseKey,
  anchorNode,
  user,
  onAttached,
  onClose,
}) {
  const [availableNodes, setAvailableNodes] = useState([]);
  const [existingRelatedKeys, setExistingRelatedKeys] = useState([]);
  const [filterTerm, setFilterTerm] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [isLinkingSelected, setIsLinkingSelected] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const anchorNodeKey = anchorNode?.key || null;
  const isBusy = isLinkingSelected || isCreating;
  const resolvedTypeKeys = useMemo(
    () => normalizeTypeKeys({ typeKey, typeKeys }),
    [typeKey, typeKeys],
  );
  const resolvedTypeKeysKey = resolvedTypeKeys.join('|');
  const createTypeKey = String(typeKey || resolvedTypeKeys[0] || '').trim();

  useEffect(() => {
    if (!isOpen || !isActive) return;

    setFilterTerm('');
    setNewTitle('');
    setTitleManuallyEdited(false);
    setSelectedKeys(new Set());
    setIsLinkingSelected(false);
    setIsCreating(false);
    setCreateError('');
  }, [isOpen, isActive, resolvedTypeKeysKey, sourceType]);

  useEffect(() => {
    if (!isActive || titleManuallyEdited) return;
    setNewTitle(String(filterTerm || '').trim());
  }, [filterTerm, isActive, titleManuallyEdited]);

  useEffect(() => {
    if (!isOpen || !isActive || !anchorNodeKey) {
      setExistingRelatedKeys([]);
      return undefined;
    }

    let cancelled = false;

    const loadExistingRelatedKeys = async () => {
      try {
        const relatedKeys = await listAdjacentNodeKeys(anchorNodeKey, { baseKey });
        if (cancelled) return;
        setExistingRelatedKeys(Array.isArray(relatedKeys) ? relatedKeys : []);
      } catch (error) {
        if (cancelled) return;
        setExistingRelatedKeys([]);
      }
    };

    loadExistingRelatedKeys();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isActive, anchorNodeKey, baseKey]);

  useEffect(() => {
    if (!isOpen || !isActive || !baseKey || resolvedTypeKeys.length === 0) {
      setAvailableNodes([]);
      return undefined;
    }

    const buckets = new Map(resolvedTypeKeys.map((key) => [key, []]));

    const emit = () => {
      const merged = [];
      buckets.forEach((rows) => {
        (Array.isArray(rows) ? rows : []).forEach((row) => {
          if (row?.key) merged.push(row);
        });
      });
      setAvailableNodes(merged);
    };

    const unsubscribers = resolvedTypeKeys.map((key) => (
      subscribeToNodesByDatabase(
        { type: key, baseKey },
        (rows) => {
          buckets.set(key, Array.isArray(rows) ? rows : []);
          emit();
        },
      )
    ));

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe?.());
    };
  }, [isOpen, isActive, baseKey, resolvedTypeKeysKey]);

  const filteredNodes = useMemo(() => {
    const normalizedFilter = String(filterTerm || '').trim().toLowerCase();
    const relatedSet = new Set(existingRelatedKeys);

    return (Array.isArray(availableNodes) ? availableNodes : [])
      .filter((entry) => {
        const key = String(entry?.key || '').trim();
        if (!key || key === anchorNodeKey) return false;
        if (relatedSet.has(key)) return false;
        if (!nodeMatchesSourceType(entry, sourceType)) return false;
        if (!normalizedFilter) return true;
        return getNodeTitle(entry).toLowerCase().includes(normalizedFilter);
      })
      .sort((left, right) => getNodeTitle(left).localeCompare(getNodeTitle(right)));
  }, [availableNodes, anchorNodeKey, existingRelatedKeys, filterTerm, sourceType]);

  const selectedCount = selectedKeys.size;

  const handleFilterChange = useCallback((value) => {
    setFilterTerm(value);
  }, []);

  const handleNewTitleChange = useCallback((value) => {
    setTitleManuallyEdited(true);
    setNewTitle(value);
  }, []);

  const handleToggleSelected = useCallback((node) => {
    const key = String(node?.key || '').trim();
    if (!key || isBusy) return;

    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, [isBusy]);

  const isNodeSelected = useCallback((nodeKey) => (
    selectedKeys.has(String(nodeKey || '').trim())
  ), [selectedKeys]);

  const handleLinkSelected = useCallback(async () => {
    if (isBusy || !anchorNodeKey || selectedCount === 0) return;

    const nodesByKey = new Map(
      filteredNodes.map((node) => [String(node?.key || '').trim(), node]),
    );
    const keysToLink = Array.from(selectedKeys).filter((key) => nodesByKey.has(key));
    if (!keysToLink.length) return;

    setIsLinkingSelected(true);

    let linkedCount = 0;
    let alreadyRelatedCount = 0;
    const newlyRelatedKeys = [];
    const failures = [];

    try {
      for (const key of keysToLink) {
        const node = nodesByKey.get(key);
        if (!node?.key) continue;

        try {
          const result = await upsertEdge(
            anchorNodeKey,
            node.key,
            buildHierarchyEdgeOptions({
              parentNode: anchorNode,
              childNode: node,
              baseKey,
            }),
          );

          if (result?.status === 'noop') {
            alreadyRelatedCount += 1;
            newlyRelatedKeys.push(node.key);
            continue;
          }

          // 'linked' and healed legacy edges both count as a successful attach.
          linkedCount += 1;
          newlyRelatedKeys.push(node.key);
        } catch (error) {
          console.warn('[AddModal] failed to attach existing node:', error);
          failures.push(error?.message || 'Could not attach item.');
        }
      }

      if (newlyRelatedKeys.length > 0) {
        setExistingRelatedKeys((prev) => {
          const next = new Set(prev);
          newlyRelatedKeys.forEach((relatedKey) => next.add(relatedKey));
          return Array.from(next);
        });
      }

      if (linkedCount > 0) {
        keysToLink.forEach((key) => {
          const node = nodesByKey.get(key);
          if (node?.key) onAttached?.(node);
        });
        onClose?.();
        toast.success(linkedCount === 1 ? 'Attached.' : `Attached ${linkedCount} items.`);
        return;
      }

      if (alreadyRelatedCount > 0 && failures.length === 0) {
        setSelectedKeys(new Set());
        toast.info(
          alreadyRelatedCount === 1
            ? 'That item is already attached.'
            : 'Those items are already attached.',
        );
        return;
      }

      if (failures.length > 0) {
        toast.error(failures[0]);
        return;
      }

      toast.error('Could not attach the selected items.');
      setSelectedKeys(new Set());
    } finally {
      setIsLinkingSelected(false);
    }
  }, [
    anchorNodeKey,
    filteredNodes,
    isBusy,
    onAttached,
    onClose,
    selectedCount,
    selectedKeys,
    baseKey,
  ]);

  const handleCreateAndLink = useCallback(async () => {
    if (isBusy || !createTypeKey || !user?.key || !anchorNodeKey) return;

    const trimmedTitle = String(newTitle || '').trim();
    if (!trimmedTitle) {
      setCreateError('Title is required.');
      return;
    }

    setCreateError('');
    setIsCreating(true);

    try {
      const newNode = await createNodeFromType(createTypeKey, user, baseKey);
      newNode.title = trimmedTitle;
      newNode.parentTitle = anchorNode?.title || '';
      newNode.parentType = anchorNode?.type || null;
      newNode.profileRoot = false;
      newNode.contributorKey = user.key;

      await createNodeDoc(newNode.key, newNode);
      await upsertEdge(
        anchorNodeKey,
        newNode.key,
        buildHierarchyEdgeOptions({
          parentNode: anchorNode,
          childNode: newNode,
          baseKey: baseKey || anchorNode?.baseKey || newNode?.baseKey || null,
        }),
      );

      onAttached?.(newNode);
      onClose?.();
      toast.success('Linked.');
    } catch (error) {
      console.warn('[AddModal] failed to create and link node:', error);
      setCreateError(error?.message || 'Could not create item.');
    } finally {
      setIsCreating(false);
    }
  }, [
    anchorNode,
    anchorNodeKey,
    createTypeKey,
    isBusy,
    newTitle,
    onAttached,
    onClose,
    user,
    baseKey,
  ]);

  return {
    filterTerm,
    handleFilterChange,
    filteredNodes,
    newTitle,
    handleNewTitleChange,
    isBusy,
    isCreating,
    isLinkingSelected,
    createError,
    selectedCount,
    isNodeSelected,
    handleToggleSelected,
    handleLinkSelected,
    handleCreateAndLink,
    titleSeededFromFilter: Boolean(String(filterTerm || '').trim()) && !titleManuallyEdited,
  };
}
